/**
 * Employee self-service "My Leave" resolver hook (Phase 5b).
 *
 * Mirrors `use_my_pay.ts` structurally: self-fetching, bearer-only,
 * no mock fallback.  Uses the real personal tRPC client (getTrpcClient())
 * with the `personalPayroll.*` ESS procedures added in Phase 4
 * (#wagepro-leave-phase4).
 *
 * Two reads, one reactive:
 *   1. `personalPayroll.listMyLeaveBalances` — balance snapshot + active
 *      leave types for the request form.  Fires whenever `selectedEntityId`
 *      changes or `reload_token` bumps.
 *   2. `personalPayroll.listMyLeaveRequests` — the employee's own request
 *      history.  Same dependencies as the balances call.
 *
 * Both procedures are gated behind requireAal2 (same as listPayslips), so
 * a 403 with `aal2Required: true` resolves `step_up_required` on EITHER
 * call.
 *
 * `submitLeaveRequest`/`cancelMyLeaveRequest` are write mutations returned
 * as async callbacks; they re-fetch both reads on success so the balance
 * cards and history update without a manual retry.
 *
 * ── No mock fallback (same rationale as use_my_pay.ts) ──────────────────
 *
 * Showing a mock leave balance as a real employee's entitlement would be
 * fabricated financial/HR data.  Every failure surfaces as a typed
 * `MyLeaveState` variant instead.
 *
 * ── Why entity_id comes from listEmployerLinks ──────────────────────────
 *
 * The personal token carries no entity_id claim — the employer link table
 * is the only authoritative source.  We re-use the same
 * `personalPayroll.listEmployerLinks` call my_pay already makes, scoped
 * to whichever employer the employee picks (first link as default).
 *
 * ── Cached across tab switches (stale-while-revalidate) ─────────────────
 *
 * The screen unmounts on every ESS tab switch, so the last good links and
 * per-employer figures are kept in memory (`my_leave_cache.ts`, keyed by
 * the signed-in user id) and seeded into state on mount. With a cached copy
 * the mount fetch is a background refresh: no skeleton, and a transient
 * failure keeps the cached figures on screen, exactly like the in-mount
 * silent refresh below.
 */
import * as React from 'react';
import { getTrpcClient } from '../lib/trpc.js';
import { isBadRequestTrpcError } from '../lib/trpc.js';
import { isAal2Required } from '../lib/auth/step_up.js';
import { useSignedInUserId } from '../lib/auth/signed_in_user_context.js';
import {
  forget_my_leave_cache,
  read_my_leave_entity_data,
  read_my_leave_links,
  write_my_leave_entity_data,
  write_my_leave_links,
} from './my_leave_cache.js';
import type {
  MyLeaveBalance,
  MyLeaveRequest,
  MyLeaveType,
} from '../screens/my_leave/my_leave_utils.js';

/** How either fetch can fail, matching the pattern in wagepro_store_my_pay.ts. */
export type MyLeaveLoadFailure = 'step_up' | 'error';

/** BAD_REQUEST payloads from submitLeaveRequest (overlap / invalid type). */
export type MyLeaveSubmitError = 'overlap' | 'invalid_type' | 'unknown';

/** Background re-fetch cadence while the My Leave tab is visible. */
export const MY_LEAVE_POLL_MS = 60_000;

/** Refocus events closer together than this share one refresh. */
export const MY_LEAVE_REFRESH_DEBOUNCE_MS = 2_000;

function classify_load_failure(err: unknown): MyLeaveLoadFailure {
  return isAal2Required(err) ? 'step_up' : 'error';
}

/** The tRPC error code on `err` (`err.data.code`), duck-typed. */
function trpc_error_code(err: unknown): string | null {
  if (typeof err !== 'object' || err === null || !('data' in err)) return null;
  const data: unknown = err.data;
  if (typeof data !== 'object' || data === null || !('code' in data)) {
    return null;
  }
  return typeof data.code === 'string' ? data.code : null;
}

/**
 * Whether a load failure means this session can no longer read the data at
 * all: an expired/revoked session (UNAUTHORIZED), access withdrawn
 * (FORBIDDEN) or a step-up gate, as opposed to a transient network / server
 * blip. Only a transient failure may be swallowed by a background refresh;
 * a terminal one drops the cached copy and surfaces, so a shared phone never
 * keeps showing figures the session is no longer allowed to see.
 */
function is_terminal_load_failure(err: unknown): boolean {
  if (isAal2Required(err)) return true;
  const code = trpc_error_code(err);
  return code === 'UNAUTHORIZED' || code === 'FORBIDDEN';
}

function classify_submit_error(err: unknown): MyLeaveSubmitError {
  if (!isBadRequestTrpcError(err)) return 'unknown';
  const msg = err instanceof Error ? err.message.toLowerCase() : '';
  if (msg.includes('overlap')) return 'overlap';
  if (msg.includes('leave type') || msg.includes('leave_type'))
    return 'invalid_type';
  return 'unknown';
}

/**
 * The wire shape returned by listMyLeaveBalances — locally declared, never
 * imported from apps/api (docs/architecture-boundaries.md).
 */
interface BalancesWireResult {
  balances: Array<{
    id: string;
    entity_id: string;
    employee_id: string;
    leave_type: string;
    balance_days_scaled: number;
    as_of_pay_run_id: string | null;
    updated_at: string;
    total_days_scaled: number;
    taken_days_scaled: number;
    /**
     * Approved future leave. Optional on the wire so a web build that
     * reaches an API from before the field shipped (rolling deploy) reads 0.
     */
    booked_days_scaled?: number;
    paid_out_days_scaled: number;
    pending_days_scaled: number;
    available_days_scaled: number;
  }>;
  leave_types: Array<{
    id: string;
    code: string;
    name: string;
    is_paid: number;
    tracks_balance: number;
    accrual_method: string;
    annual_entitlement_days_scaled: number | null;
    is_system: number;
  }>;
}

/** Wire shape from listMyLeaveRequests. */
interface RequestWireRow {
  id: string;
  employee_id: string;
  leave_type: string;
  start_date: string;
  end_date: string;
  days_requested: number;
  status: string;
  notes: string | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * The My Leave screen state — structurally mirrors `MyPayState`:
 *
 * - `loading`          — at least one fetch not yet settled.
 * - `step_up_required` — either fetch rejected with aal2Required: true.
 * - `error`            — a non-step-up failure on either fetch.
 * - `no_employer_link` — listEmployerLinks returned an empty list.
 * - `ready`            — both fetches settled successfully.
 */
export type MyLeaveState =
  | { kind: 'loading' }
  | { kind: 'step_up_required' }
  | { kind: 'error' }
  | { kind: 'no_employer_link' }
  | {
      kind: 'ready';
      balances: MyLeaveBalance[];
      leave_types: MyLeaveType[];
      requests: MyLeaveRequest[];
      entity_id: string;
      employer_name: string;
    };

export interface MyLeaveSubmitInput {
  leave_type: string;
  start_date: string;
  end_date: string;
  days_requested: number;
  notes?: string;
}

export interface MyLeaveSubmitResult {
  outcome: 'success';
  request_id: string;
}

export interface MyLeaveSubmitFailure {
  outcome: 'failure';
  error: MyLeaveSubmitError;
  /** The raw server message (safe to surface for overlap/type errors). */
  message: string | null;
}

export type MyLeaveActionResult = MyLeaveSubmitResult | MyLeaveSubmitFailure;

export interface MyLeaveCancelResult {
  outcome: 'success' | 'failure';
  message?: string | null;
}

export interface UseMyLeaveResult {
  state: MyLeaveState;
  /** Re-runs both fetches from scratch. Only meaningful for `error`. */
  retry: () => void;
  /**
   * Submit a new leave request.  Resolves the result shape — never throws.
   * Triggers a re-fetch on success so balances + history update immediately.
   */
  submit: (input: MyLeaveSubmitInput) => Promise<MyLeaveActionResult>;
  /**
   * Cancel a pending own request.  Triggers a re-fetch on success.
   * Resolves `{ outcome: 'failure', message }` for non-cancellable states.
   */
  cancel: (request_id: string) => Promise<MyLeaveCancelResult>;
}

/** Employer link row subset the hook uses (mirrors wagepro_store_my_pay.ts). */
interface EmployerLink {
  entity_id: string;
  entity_name: string;
}

export function useMyLeave(): UseMyLeaveResult {
  // Seed from the in-memory cache (only ever the signed-in user's own copy).
  // Pinned to the user signed in at mount: if that ever changes under a
  // mounted screen, caching is off for the rest of this mount (null never
  // reads or writes) so one user's figures cannot be filed under another.
  const live_user_id = useSignedInUserId();
  const [mount_user_id] = React.useState(live_user_id);
  const user_id = live_user_id === mount_user_id ? live_user_id : null;
  const [seed] = React.useState(() => {
    const cached_links = read_my_leave_links(user_id);
    const entity_id = cached_links?.selected_entity_id ?? null;
    return {
      links: cached_links?.links,
      selected_entity_id: entity_id,
      data:
        entity_id === null
          ? null
          : read_my_leave_entity_data(user_id, entity_id),
    };
  });

  // Phase 1: resolve the employer link (same pattern as use_my_pay.ts).
  const [links, set_links] = React.useState<EmployerLink[] | undefined>(
    seed.links,
  );
  const [links_failure, set_links_failure] = React.useState<
    MyLeaveLoadFailure | undefined
  >(undefined);
  const [selected_entity_id, set_selected_entity_id] = React.useState<
    string | null
  >(seed.selected_entity_id);

  // Phase 2: per-entity balance + request fetches.
  const [balances, set_balances] = React.useState<MyLeaveBalance[] | undefined>(
    seed.data?.balances,
  );
  const [leave_types, set_leave_types] = React.useState<
    MyLeaveType[] | undefined
  >(seed.data?.leave_types);
  const [requests, set_requests] = React.useState<MyLeaveRequest[] | undefined>(
    seed.data?.requests,
  );
  // Which employer `balances`/`leave_types`/`requests` belong to — set in the
  // same batch as the figures, so the cache write below can never file one
  // employer's figures under another's id mid-switch.
  const [data_entity_id, set_data_entity_id] = React.useState<string | null>(
    seed.data === null ? null : seed.selected_entity_id,
  );
  const [data_failure, set_data_failure] = React.useState<
    MyLeaveLoadFailure | undefined
  >(undefined);

  const [reload_token, set_reload_token] = React.useState(0);
  // Bumped for a background re-fetch of balances + requests that keeps the
  // current figures on screen (no skeleton flash) — after the employee's own
  // submit/cancel, and when an employer-side approve / reject / cancel /
  // adjustment may have moved the balance (tab refocus + a slow poll).
  const [refresh_token, set_refresh_token] = React.useState(0);
  // A silent run is honoured only for the entity whose data is already on
  // screen: `silent_request_ref` names the entity the refresh was asked for,
  // `loaded_entity_ref` the entity whose balances/requests are displayed.
  // An employer switch batched with a refresh therefore still clears and
  // shows the skeleton instead of leaving the previous employer's figures up.
  const silent_request_ref = React.useRef<string | null>(null);
  const loaded_entity_ref = React.useRef<string | null>(data_entity_id);
  const last_refresh_at_ref = React.useRef(0);

  // ── Effect 1: load employer links ─────────────────────────────────────────
  // With cached links the first run is a background refresh: the cached
  // list stays up. A retry (reload_token bump) still starts from scratch.
  const has_seed_links = seed.links !== undefined;
  React.useEffect(() => {
    let cancelled = false;
    const background = reload_token === 0 && has_seed_links;
    if (!background) set_links(undefined);
    set_links_failure(undefined);

    async function load_links(): Promise<void> {
      try {
        const result =
          await getTrpcClient().personalPayroll.listEmployerLinks.query();
        if (cancelled) return;
        set_links(result.links);
        const default_entity_id: string | null =
          result.links.at(0)?.entity_id ?? null;
        set_selected_entity_id((current) =>
          current !== null && result.links.some((l) => l.entity_id === current)
            ? current
            : default_entity_id,
        );
      } catch (err) {
        if (cancelled) return;
        const failure = classify_load_failure(err);
        // Same rule as the balances refresh: a transient failure keeps the
        // cached list; a terminal one drops the cache and surfaces.
        const terminal = is_terminal_load_failure(err);
        if (terminal) forget_my_leave_cache(user_id);
        if (background && !terminal) return;
        set_links_failure(failure);
      }
    }

    void load_links();
    return () => {
      cancelled = true;
    };
  }, [reload_token, has_seed_links, user_id]);

  // Keep the cache's copy of the links + selected employer current. Never
  // while a load has failed: a terminal failure has just forgotten the
  // cache, and a links response landing after it must not re-create it.
  React.useEffect(() => {
    if (links === undefined) return;
    if (links_failure !== undefined || data_failure !== undefined) return;
    write_my_leave_links(user_id, links, selected_entity_id);
  }, [user_id, links, selected_entity_id, links_failure, data_failure]);

  // ── Effect 2: load balances + requests ────────────────────────────────────
  React.useEffect(() => {
    if (selected_entity_id === null) return;
    const entity_id: string = selected_entity_id;
    let cancelled = false;
    let silent =
      silent_request_ref.current === entity_id &&
      loaded_entity_ref.current === entity_id;
    silent_request_ref.current = null;
    if (!silent) {
      set_data_failure(undefined);
      // A retry (reload_token bump) starts from scratch, like Effect 1, so
      // its own failure surfaces instead of hiding behind cached figures.
      const cached =
        reload_token === 0
          ? read_my_leave_entity_data(user_id, entity_id)
          : null;
      if (cached === null) {
        loaded_entity_ref.current = null;
        set_data_entity_id(null);
        set_balances(undefined);
        set_leave_types(undefined);
        set_requests(undefined);
      } else {
        // Show this employer's cached figures now; refresh behind them.
        loaded_entity_ref.current = entity_id;
        set_data_entity_id(entity_id);
        set_balances(cached.balances);
        set_leave_types(cached.leave_types);
        set_requests(cached.requests);
        last_refresh_at_ref.current = Date.now();
        silent = true;
      }
    }

    async function load_data(): Promise<void> {
      try {
        const [balances_result, requests_result] = await Promise.all([
          getTrpcClient().personalPayroll.listMyLeaveBalances.query({
            entity_id,
          }) as Promise<BalancesWireResult>,
          getTrpcClient().personalPayroll.listMyLeaveRequests.query({
            entity_id,
          }) as Promise<RequestWireRow[]>,
        ]);
        if (cancelled) return;

        set_balances(
          balances_result.balances.map((b) => ({
            leave_type: b.leave_type,
            balance_days_scaled: b.balance_days_scaled,
            total_days_scaled: b.total_days_scaled,
            taken_days_scaled: b.taken_days_scaled,
            booked_days_scaled: b.booked_days_scaled ?? 0,
            pending_days_scaled: b.pending_days_scaled,
            available_days_scaled: b.available_days_scaled,
          })),
        );
        set_leave_types(
          balances_result.leave_types.map((lt) => ({
            code: lt.code,
            label: lt.name,
          })),
        );
        set_requests(
          requests_result.map((r) => ({
            id: r.id,
            leave_type: r.leave_type,
            start_date: r.start_date,
            end_date: r.end_date,
            days_requested: r.days_requested,
            status: r.status,
            notes: r.notes,
            rejection_reason: r.rejection_reason,
            created_at: r.created_at,
          })),
        );
        set_data_entity_id(entity_id);
        loaded_entity_ref.current = entity_id;
      } catch (err) {
        if (cancelled) return;
        const failure = classify_load_failure(err);
        // A background refresh that hits a transient error keeps the last
        // good figures (and any half-filled form) on screen; the next poll
        // or refocus retries. A terminal failure (session expired, access
        // withdrawn, step-up) drops the cached and displayed figures and
        // surfaces: the session can no longer read this data at all.
        const terminal = is_terminal_load_failure(err);
        if (silent && !terminal) return;
        if (terminal) {
          forget_my_leave_cache(user_id);
          loaded_entity_ref.current = null;
          set_data_entity_id(null);
          set_balances(undefined);
          set_leave_types(undefined);
          set_requests(undefined);
        }
        set_data_failure(failure);
      }
    }

    void load_data();
    return () => {
      cancelled = true;
    };
  }, [selected_entity_id, reload_token, refresh_token, user_id]);

  // Keep the cache's copy of the displayed employer's figures current.
  React.useEffect(() => {
    if (
      data_entity_id === null ||
      balances === undefined ||
      leave_types === undefined ||
      requests === undefined
    ) {
      return;
    }
    write_my_leave_entity_data(user_id, data_entity_id, {
      balances,
      leave_types,
      requests,
    });
  }, [user_id, data_entity_id, balances, leave_types, requests]);

  const refresh_silently = React.useCallback(() => {
    last_refresh_at_ref.current = Date.now();
    silent_request_ref.current = selected_entity_id;
    set_refresh_token((t) => t + 1);
  }, [selected_entity_id]);

  // ── Derived state ─────────────────────────────────────────────────────────

  const state = React.useMemo((): MyLeaveState => {
    // Fail-closed: step_up outranks error outranks loading.
    if (links_failure === 'step_up' || data_failure === 'step_up') {
      return { kind: 'step_up_required' };
    }
    if (links_failure === 'error' || data_failure === 'error') {
      return { kind: 'error' };
    }
    if (links === undefined) return { kind: 'loading' };
    if (links.length === 0) return { kind: 'no_employer_link' };
    if (selected_entity_id === null) return { kind: 'loading' };
    if (
      balances === undefined ||
      leave_types === undefined ||
      requests === undefined ||
      // Never show one employer's figures under another employer's name
      // (the render between an employer switch and Effect 2 clearing).
      data_entity_id !== selected_entity_id
    ) {
      return { kind: 'loading' };
    }
    const link = links.find((l) => l.entity_id === selected_entity_id);
    return {
      kind: 'ready',
      balances,
      leave_types,
      requests,
      entity_id: selected_entity_id,
      employer_name: link?.entity_name ?? '—',
    };
  }, [
    links,
    links_failure,
    selected_entity_id,
    balances,
    leave_types,
    requests,
    data_entity_id,
    data_failure,
  ]);

  // ── Effect 3: pick up employer-side changes ───────────────────────────────
  // Approvals, rejections, cancellations and balance adjustments happen in
  // the employer app, so re-fetch when the employee returns to the tab and
  // on a slow poll while it is visible. Only while `ready`: in step-up or
  // error a poll would just repeat a request that is bound to fail.
  // Returning to a tab fires both `focus` and `visibilitychange`; the
  // debounce collapses them into one request.
  const is_ready = state.kind === 'ready';
  React.useEffect(() => {
    if (!is_ready) return;
    const on_visible = (): void => {
      if (document.visibilityState !== 'visible') return;
      if (
        Date.now() - last_refresh_at_ref.current <
        MY_LEAVE_REFRESH_DEBOUNCE_MS
      )
        return;
      refresh_silently();
    };
    const interval = window.setInterval(on_visible, MY_LEAVE_POLL_MS);
    document.addEventListener('visibilitychange', on_visible);
    window.addEventListener('focus', on_visible);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', on_visible);
      window.removeEventListener('focus', on_visible);
    };
  }, [is_ready, refresh_silently]);

  // ── Actions ───────────────────────────────────────────────────────────────

  const retry = React.useCallback(() => {
    set_reload_token((t) => t + 1);
  }, []);

  const submit = React.useCallback(
    async (input: MyLeaveSubmitInput): Promise<MyLeaveActionResult> => {
      if (selected_entity_id === null) {
        return { outcome: 'failure', error: 'unknown', message: null };
      }
      try {
        const result =
          await getTrpcClient().personalPayroll.submitLeaveRequest.mutate({
            entity_id: selected_entity_id,
            leave_type: input.leave_type,
            start_date: input.start_date,
            end_date: input.end_date,
            days_requested: input.days_requested,
            notes: input.notes,
          });
        // Refresh balances + history, keeping the current figures on screen.
        refresh_silently();
        return { outcome: 'success', request_id: result.request_id };
      } catch (err) {
        return {
          outcome: 'failure',
          error: classify_submit_error(err),
          message: err instanceof Error ? err.message : null,
        };
      }
    },
    [selected_entity_id, refresh_silently],
  );

  const cancel = React.useCallback(
    async (request_id: string): Promise<MyLeaveCancelResult> => {
      if (selected_entity_id === null) {
        return { outcome: 'failure', message: null };
      }
      try {
        await getTrpcClient().personalPayroll.cancelMyLeaveRequest.mutate({
          entity_id: selected_entity_id,
          request_id,
        });
        refresh_silently();
        return { outcome: 'success' };
      } catch (err) {
        return {
          outcome: 'failure',
          message: err instanceof Error ? err.message : null,
        };
      }
    },
    [selected_entity_id, refresh_silently],
  );

  return { state, retry, submit, cancel };
}
